/**
 * FacturacionPage - CFDI 4.0 고객용 세금계산서 셀프 발급 포털 (#877)
 *
 * 매장 영수증 하단 QR 코드 스캔 또는 URL 진입 시:
 * 1. 티켓 조회 (Folio, Date, Total, Store ID 기반)
 * 2. SAT 납세자 정보(RFC, 상호, C.P., 과세체계, Uso CFDI) 입력
 * 3. SW Sapien PAC 실시간 발급 후 XML/PDF 다운로드
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  facturacionApi,
  type CfdiInvoiceData,
  type FacturacionLookupResponse,
} from '@/lib/api/facturacion';
import { ApiClientError } from '@/lib/api';
import { FileText, Download, CheckCircle2, AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react';
import { ROUTES } from '@/config/routes';

const RFC_REGEX = /^[A-Z&Ñ]{3,4}\d{6}[A-Z0-9]{3}$/i;

const REGIMENES = [
  { code: '601', label: '601 - General de Ley Personas Morales' },
  { code: '612', label: '612 - Personas Físicas con Actividades Empresariales' },
  { code: '616', label: '616 - Sin obligaciones fiscales' },
  { code: '626', label: '626 - Régimen Simplificado de Confianza (RESICO)' },
];

const USOS_CFDI = [
  { code: 'G01', label: 'G01 - Adquisición de mercancías' },
  { code: 'G03', label: 'G03 - Gastos en general' },
  { code: 'S01', label: 'S01 - Sin efectos fiscales' },
];

// admin#2639: lookup 실패 404 단일 처리 — 후보 없음·합계 불일치를 구분하지 않고
// 같은 안내로 답한다(서버도 동일 404로 통일). 409 복수후보는 사용자가 주문을
// 특정할 수 있도록 별도 안내를 유지한다.
function resolveLookupErrorMessage(err: unknown): string {
  if (err instanceof ApiClientError && err.status === 404) {
    return 'Ticket no encontrado. Verifica el folio, el total y la fecha impresos en tu ticket.';
  }
  if (err instanceof ApiClientError && err.status === 409) {
    return 'Varios pedidos coinciden con esos datos. Escanea el QR del ticket o agrega el ID del pedido para identificarlo.';
  }
  if (err instanceof ApiClientError) {
    return err.message || 'Ticket no encontrado o no cumple las condiciones para facturación.';
  }
  if (err instanceof Error) {
    return err.message;
  }
  return 'Error al consultar el ticket. Verifique los datos.';
}

export function FacturacionPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // 검색 파라미터 상태
  const storeId = searchParams.get('store_id') || '1';
  const [orderNumber, setOrderNumber] = useState<string>(
    searchParams.get('folio') || searchParams.get('order_number') || ''
  );
  const orderId = searchParams.get('order_id') || '';
  const [total, setTotal] = useState<string>(searchParams.get('total') || '');
  const [date, setDate] = useState<string>(
    searchParams.get('date') || new Date().toISOString().split('T')[0]
  );

  // 진행 단계 상태
  const [isLoading, setIsLoading] = useState(false);
  const [lookupData, setLookupData] = useState<FacturacionLookupResponse['data'] | null>(null);
  const [activeInvoice, setActiveInvoice] = useState<CfdiInvoiceData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 세무정보 입력 폼 상태
  const [rfc, setRfc] = useState('');
  const [razonSocial, setRazonSocial] = useState('');
  const [codigoPostal, setCodigoPostal] = useState('');
  const [regimenFiscal, setRegimenFiscal] = useState('616');
  const [usoCfdi, setUsoCfdi] = useState('G03');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 로컬스토리지에서 최근 입력 정보 불러오기
  useEffect(() => {
    try {
      const saved = localStorage.getItem('olulo_customer_tax_profile');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.rfc) setRfc(parsed.rfc);
        if (parsed.razonSocial) setRazonSocial(parsed.razonSocial);
        if (parsed.codigoPostal) setCodigoPostal(parsed.codigoPostal);
        if (parsed.regimenFiscal) setRegimenFiscal(parsed.regimenFiscal);
        if (parsed.usoCfdi) setUsoCfdi(parsed.usoCfdi);
        if (parsed.email) setEmail(parsed.email);
      }
    } catch {
      // ignore
    }
  }, []);

  // QR 코드 등으로 파라미터가 유효하게 넘어왔을 때 자동 조회
  useEffect(() => {
    const hasOrderIdentifier = orderId || orderNumber;
    if (hasOrderIdentifier && total) {
      void handleLookup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // admin#2639: 조회 성공 시 lookup_token(15분)을 lookupData state에 함께 보관한다.
  // 토큰은 인메모리에만 두며, 발급 실패(404) 시 재조회 후 재시도 경로에서도 재사용한다.
  const performLookup = async (): Promise<FacturacionLookupResponse['data']> => {
    const numericTotal = parseFloat(total);
    if (isNaN(numericTotal) || numericTotal <= 0) {
      throw new Error('Ingrese un importe total válido.');
    }

    const res = await facturacionApi.lookupOrder({
      store_id: storeId ? parseInt(storeId, 10) : 1,
      order_id: orderId ? parseInt(orderId, 10) : undefined,
      order_number: orderNumber || undefined,
      total: numericTotal,
      date: date || undefined,
    });

    setLookupData(res.data);

    if (res.data.invoice) {
      setActiveInvoice(res.data.invoice);
    }

    return res.data;
  };

  const handleLookup = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setErrorMessage(null);
    setIsLoading(true);

    try {
      await performLookup();
    } catch (err: unknown) {
      setErrorMessage(resolveLookupErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleIssueInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    const cleanRfc = rfc.trim().toUpperCase();
    const cleanRazon = razonSocial.trim().toUpperCase();
    const cleanCp = codigoPostal.trim();

    if (!cleanRfc || !RFC_REGEX.test(cleanRfc)) {
      setErrorMessage('El RFC ingresado no es válido (debe tener 12 o 13 caracteres alfanuméricos).');
      return;
    }
    if (!cleanRazon) {
      setErrorMessage('Ingrese la Razón Social o Nombre del receptor.');
      return;
    }
    if (!/^\d{5}$/.test(cleanCp)) {
      setErrorMessage('El Código Postal debe ser de 5 dígitos.');
      return;
    }

    // 로컬스토리지 저장
    try {
      localStorage.setItem(
        'olulo_customer_tax_profile',
        JSON.stringify({
          rfc: cleanRfc,
          razonSocial: cleanRazon,
          codigoPostal: cleanCp,
          regimenFiscal,
          usoCfdi,
          email: email.trim(),
        })
      );
    } catch {
      // ignore
    }

    const targetOrderId = lookupData?.order.id || orderId;
    const lookupToken = lookupData?.lookup_token;
    if (!targetOrderId) {
      setErrorMessage('No se encontró el identificador del pedido.');
      return;
    }
    if (!lookupToken) {
      // admin#2639: 토큰 없이 발급을 호출하면 서버가 거부하므로 재조회부터 안내한다.
      setErrorMessage('Falta la verificación del ticket. Busca tu ticket nuevamente para continuar.');
      return;
    }

    setIsSubmitting(true);

    const fiscalPayload = {
      rfc: cleanRfc,
      razon_social: cleanRazon,
      codigo_postal: cleanCp,
      regimen_fiscal: regimenFiscal,
      uso_cfdi: usoCfdi,
      email: email.trim() || undefined,
    };

    try {
      try {
        const res = await facturacionApi.issueInvoice(targetOrderId, {
          ...fiscalPayload,
          lookup_token: lookupToken,
        });

        setActiveInvoice(res.data);
      } catch (err: unknown) {
        // admin#2639: 404는 토큰 만료(15분) 가능성이 크므로 재조회 후 1회만
        // 재시도한다. 404가 아닌 실패(422·429 등)는 그대로 노출한다.
        if (!(err instanceof ApiClientError) || err.status !== 404) {
          throw err;
        }
        const fresh = await performLookup();
        const retry = await facturacionApi.issueInvoice(fresh.order.id, {
          ...fiscalPayload,
          lookup_token: fresh.lookup_token,
        });

        setActiveInvoice(retry.data);
      }
    } catch (err: unknown) {
      if (err instanceof ApiClientError && err.status === 404) {
        setErrorMessage(
          'No fue posible emitir la factura con esos datos. Verifica tu ticket e inténtalo de nuevo.'
        );
      } else if (err instanceof ApiClientError) {
        setErrorMessage(err.message || 'Error al timbrar la factura ante el SAT.');
      } else if (err instanceof Error) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage('Error al emitir el CFDI. Por favor intente nuevamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadXml = () => {
    if (!activeInvoice) return;
    if (activeInvoice.xml_url?.startsWith('http')) {
      window.open(activeInvoice.xml_url, '_blank');
    } else if (activeInvoice.xml_url) {
      const blob = new Blob([activeInvoice.xml_url], { type: 'application/xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CFDI_${activeInvoice.uuid}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleDownloadPdf = () => {
    if (activeInvoice?.pdf_url) {
      window.open(activeInvoice.pdf_url, '_blank');
    } else {
      alert('La representación impresa (PDF) está en proceso de generación.');
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* 상단 헤더 */}
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => navigate(ROUTES.HOME)}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Volver al inicio"
        >
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Facturación Electrónica (CFDI 4.0)</h1>
          <p className="text-sm text-gray-500">
            Emisión y descarga de comprobantes fiscales de Olulo México
          </p>
        </div>
      </div>

      {/* 에러 메시지 알림 */}
      {errorMessage && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 text-red-700">
          <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-sm">Atención</p>
            <p className="text-sm mt-0.5">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* 3단계: 이미 발급 완료된 경우 (결과 및 다운로드) */}
      {activeInvoice ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 text-green-600 mb-4">
            <CheckCircle2 className="w-8 h-8 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-bold text-gray-900">Factura Emitida Correctamente</h2>
              <p className="text-sm text-gray-500">Su CFDI ha sido timbrado exitosamente ante el SAT.</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 my-4 space-y-2 text-sm">
            <div className="flex justify-between py-1 border-b border-gray-200">
              <span className="text-gray-500">Folio Fiscal (UUID):</span>
              <span className="font-mono font-medium text-gray-900 text-xs sm:text-sm">
                {activeInvoice.uuid}
              </span>
            </div>
            <div className="flex justify-between py-1 border-b border-gray-200">
              <span className="text-gray-500">RFC Receptor:</span>
              <span className="font-medium text-gray-900">{activeInvoice.rfc_receptor}</span>
            </div>
            <div className="flex justify-between py-1 border-b border-gray-200">
              <span className="text-gray-500">Razón Social:</span>
              <span className="font-medium text-gray-900">{activeInvoice.nombre_receptor}</span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-gray-500">Total Facturado:</span>
              <span className="font-bold text-gray-900 text-base">
                ${parseFloat(String(activeInvoice.total)).toFixed(2)} MXN
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
            <button
              type="button"
              onClick={handleDownloadPdf}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl transition-colors shadow-sm"
            >
              <Download className="w-5 h-5" />
              Descargar PDF
            </button>
            <button
              type="button"
              onClick={handleDownloadXml}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors shadow-sm"
            >
              <FileText className="w-5 h-5" />
              Descargar XML
            </button>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-100 flex justify-center">
            <button
              type="button"
              onClick={() => {
                setActiveInvoice(null);
                setLookupData(null);
                setOrderNumber('');
                setTotal('');
              }}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <RefreshCw className="w-4 h-4" />
              Facturar otro ticket
            </button>
          </div>
        </div>
      ) : !lookupData ? (
        /* 1단계: 티켓 조회 폼 */
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Paso 1: Consulta tu Ticket</h2>
          <p className="text-sm text-gray-500 mb-6">
            Ingresa los datos impresos en tu ticket de compra para comenzar.
          </p>

          <form onSubmit={handleLookup} className="space-y-4">
            <div>
              <label htmlFor="lookup-folio" className="block text-sm font-medium text-gray-700 mb-1">
                Folio / Número de Pedido *
              </label>
              <input
                id="lookup-folio"
                type="text"
                placeholder="Ej. ORD-2026-001 o #1042"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                required
                className="w-full px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="lookup-total" className="block text-sm font-medium text-gray-700 mb-1">
                  Total de la compra ($ MXN) *
                </label>
                <input
                  id="lookup-total"
                  type="number"
                  step="0.01"
                  placeholder="Ej. 116.00"
                  value={total}
                  onChange={(e) => setTotal(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                />
              </div>

              <div>
                <label htmlFor="lookup-date" className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha de compra
                </label>
                <input
                  id="lookup-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full mt-4 py-3 px-4 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white font-medium rounded-xl transition-colors shadow-sm flex items-center justify-center gap-2"
            >
              {isLoading && <RefreshCw className="w-4 h-4 animate-spin" />}
              {isLoading ? 'Consultando Ticket...' : 'Buscar Ticket'}
            </button>
          </form>
        </div>
      ) : (
        /* 2단계: 티켓 확인 및 납세자 정보 입력 폼 */
        <div className="space-y-6">
          {/* 티켓 요약 카드 */}
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
            <h3 className="font-bold text-gray-900 text-sm mb-2">Resumen de Compra</h3>
            <div className="flex justify-between text-sm py-1 border-b border-gray-200">
              <span className="text-gray-500">Folio:</span>
              <span className="font-medium text-gray-900">{lookupData.order.order_number}</span>
            </div>
            <div className="flex justify-between text-sm py-1 border-b border-gray-200">
              <span className="text-gray-500">Subtotal:</span>
              <span className="font-medium text-gray-900">${lookupData.order.subtotal}</span>
            </div>
            <div className="flex justify-between text-sm py-1 border-b border-gray-200">
              <span className="text-gray-500">IVA (16%):</span>
              <span className="font-medium text-gray-900">${lookupData.order.tax}</span>
            </div>
            <div className="flex justify-between text-sm py-1">
              <span className="text-gray-700 font-semibold">Total:</span>
              <span className="font-bold text-red-600 text-base">${lookupData.order.total} MXN</span>
            </div>
          </div>

          {/* 세무정보 입력 폼 */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Paso 2: Datos Fiscales del Receptor</h2>
            <p className="text-sm text-gray-500 mb-6">
              Asegúrate de ingresar los datos exactamente como aparecen en tu Constancia de Situación Fiscal.
            </p>

            <form onSubmit={handleIssueInvoice} className="space-y-4">
              <div>
                <label htmlFor="cfdi-rfc" className="block text-sm font-medium text-gray-700 mb-1">
                  RFC (Registro Federal de Contribuyentes) *
                </label>
                <input
                  id="cfdi-rfc"
                  type="text"
                  placeholder="Ej. XAXX010101000"
                  maxLength={13}
                  value={rfc}
                  onChange={(e) => setRfc(e.target.value.toUpperCase())}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-300 font-mono uppercase focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                />
              </div>

              <div>
                <label htmlFor="cfdi-nombre" className="block text-sm font-medium text-gray-700 mb-1">
                  Razón Social / Nombre Completo *
                </label>
                <input
                  id="cfdi-nombre"
                  type="text"
                  placeholder="EN MAYÚSCULAS SIN RÉGIMEN CAPITAL"
                  value={razonSocial}
                  onChange={(e) => setRazonSocial(e.target.value.toUpperCase())}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-300 uppercase focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="cfdi-cp" className="block text-sm font-medium text-gray-700 mb-1">
                    Código Postal del Domicilio Fiscal *
                  </label>
                  <input
                    id="cfdi-cp"
                    type="text"
                    maxLength={5}
                    placeholder="Ej. 06600"
                    value={codigoPostal}
                    onChange={(e) => setCodigoPostal(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-300 font-mono focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="cfdi-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Correo Electrónico (Para envío)
                  </label>
                  <input
                    id="cfdi-email"
                    type="email"
                    placeholder="cliente@ejemplo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="cfdi-regimen" className="block text-sm font-medium text-gray-700 mb-1">
                    Régimen Fiscal *
                  </label>
                  <select
                    id="cfdi-regimen"
                    value={regimenFiscal}
                    onChange={(e) => setRegimenFiscal(e.target.value)}
                    disabled={isSubmitting}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-white focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                  >
                    {REGIMENES.map((reg) => (
                      <option key={reg.code} value={reg.code}>
                        {reg.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="cfdi-uso" className="block text-sm font-medium text-gray-700 mb-1">
                    Uso de CFDI *
                  </label>
                  <select
                    id="cfdi-uso"
                    value={usoCfdi}
                    onChange={(e) => setUsoCfdi(e.target.value)}
                    disabled={isSubmitting}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-300 bg-white focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                  >
                    {USOS_CFDI.map((uso) => (
                      <option key={uso.code} value={uso.code}>
                        {uso.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setLookupData(null)}
                  disabled={isSubmitting}
                  className="w-1/3 py-3 px-4 border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium rounded-xl transition-colors"
                >
                  Atrás
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-2/3 py-3 px-4 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white font-medium rounded-xl transition-colors shadow-sm flex items-center justify-center gap-2"
                >
                  {isSubmitting && <RefreshCw className="w-4 h-4 animate-spin" />}
                  {isSubmitting ? 'Timbrando CFDI ante el SAT...' : 'Generar Factura'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
